import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import './HalloweenDecorations.css';

// SVG-декор вместо эмодзи — стабильно выглядит на любой ОС.
const Bat = () => (
  <svg viewBox="0 0 48 28" width="1em" height="1em" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M24 10c-2-5-9-9-15-8 3 2 4 5 3 8-5-1-10 1-12 5 4-1 7 0 9 2-3 1-5 3-6 6 4-2 8-2 11 0 2 1 3 3 3 5 0-2 1-4 3-5 0 2 1 4 4 5 3-1 4-3 4-5 2 1 3 3 3 5 0-2 1-4 3-5 3-2 7-2 11 0-1-3-3-5-6-6 2-2 5-3 9-2-2-4-7-6-12-5-1-3 0-6 3-8-6-1-13 3-15 8z"
      fill="#241030"
    />
  </svg>
);

const Pumpkin = () => (
  <svg viewBox="0 0 40 36" width="1em" height="1em" xmlns="http://www.w3.org/2000/svg">
    <rect x="17.5" y="0" width="5" height="8" rx="2" fill="#4a7a3a" />
    <ellipse cx="20" cy="22" rx="18" ry="14" fill="#ff7518" />
    <path d="M8 22c0-8 3-14 4-14M20 22c0-9 0-15 0-15M32 22c0-8-3-14-4-14" stroke="#c74a05" strokeWidth="1.4" fill="none" opacity="0.55" />
    <path d="M13 21l3-4 3 4h-6zM24 21l3-4 3 4h-6z" fill="#2a1420" />
    <path d="M13 26q7 4 14 0" stroke="#2a1420" strokeWidth="2" fill="none" strokeLinecap="round" />
  </svg>
);

const Star = () => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 0 L14.5 9.5 L24 12 L14.5 14.5 L12 24 L9.5 14.5 L0 12 L9.5 9.5 Z" fill="currentColor" />
  </svg>
);

const Spider = () => (
  <svg viewBox="0 0 32 32" width="1em" height="1em" xmlns="http://www.w3.org/2000/svg">
    <ellipse cx="16" cy="19" rx="6" ry="7" fill="#1a0f1f" />
    <circle cx="16" cy="10" r="4" fill="#1a0f1f" />
    <g stroke="#1a0f1f" strokeWidth="1.4" fill="none" strokeLinecap="round">
      <path d="M10 15 L2 11 M10 18 L1 18 M10 21 L3 26" />
      <path d="M22 15 L30 11 M22 18 L31 18 M22 21 L29 26" />
    </g>
  </svg>
);

const SPARKLES = [
  { kind: 'star', top: '8%', left: '3%', delay: '0s', size: '15px' },
  { kind: 'bat', top: '16%', left: '95%', delay: '0.6s', size: '20px' },
  { kind: 'star', top: '34%', left: '2%', delay: '1.2s', size: '13px' },
  { kind: 'bat', top: '48%', left: '96%', delay: '0.35s', size: '17px' },
  { kind: 'star', top: '70%', left: '4%', delay: '0.9s', size: '14px' },
  { kind: 'bat', top: '80%', left: '94%', delay: '1.5s', size: '19px' },
  { kind: 'star', top: '5%', left: '48%', delay: '0.45s', size: '13px' },
  { kind: 'star', top: '24%', left: '72%', delay: '1.1s', size: '12px' },
];

// Текст стикеров зависит от того, что сейчас на экране.
const STICKER_TEXT = {
  error: [['ой… заклинание', 'не сработало 😱'], ['глянь строки', 'с ошибкой, бу!']],
  offer: [['готово!', 'сварилось ✨'], ['КП собрано —', 'можно колдовать дальше 🎃']],
  counting: [['варю зелье…', 'считаю варианты 🧪'], ['щас всё', 'разложу по котлам']],
  empty: [['брось товар', 'в котёл ✨'], ['жду ингредиенты,', 'бу~ 👻']],
};

const BUBBLE_TEXT = {
  error: 'ой, заклинание не сработало… 😱',
  offer: 'готово! КП сварено ✨',
  counting: 'варю расчёт, бу~',
  empty: 'привет! брось товар в котёл 🎃',
};

export default function HalloweenDecorations({ status = 'empty' }) {
  const [sticky1, sticky2] = STICKER_TEXT[status] || STICKER_TEXT.empty;
  const bubble = BUBBLE_TEXT[status] || BUBBLE_TEXT.empty;

  const [reaction, setReaction] = useState(null);
  const prevStatus = useRef(status);
  useEffect(() => {
    if (status === prevStatus.current) return;
    prevStatus.current = status;
    if (status !== 'offer' && status !== 'error') return;
    setReaction(status === 'offer' ? 'happy' : 'sad');
    const t = setTimeout(() => setReaction(null), 1000);
    return () => clearTimeout(t);
  }, [status]);

  return ReactDOM.createPortal(
    <div className="hw-decor-root" aria-hidden="true">

      {/* ── Паутина в верхних углах ── */}
      <div className="hw-corner hw-corner--tl">
        <span className="hw-corner__web" />
        <span className="hw-corner__spider"><Spider /></span>
      </div>
      <div className="hw-corner hw-corner--tr">
        <span className="hw-corner__web" />
      </div>

      {/* ── Разбросанные искорки: звёзды и летучие мыши ── */}
      {SPARKLES.map((s, i) => (
        <span
          key={i}
          className={`hw-sparkle hw-sparkle--${s.kind}`}
          style={{
            top: s.top,
            left: s.left,
            fontSize: s.size,
            animationDelay: s.delay,
          }}
        >
          {s.kind === 'bat' ? <Bat /> : <Star />}
        </span>
      ))}

      {/* ── Летучие мыши, пролетающие через экран ── */}
      <span className="hw-flybat hw-flybat--1"><Bat /></span>
      <span className="hw-flybat hw-flybat--2"><Bat /></span>
      <span className="hw-flybat hw-flybat--3"><Bat /></span>

      {/* ── Стикеры (текст зависит от состояния экрана) ── */}
      <div className="hw-sticky hw-sticky--1">
        {sticky1[0]}<br />{sticky1[1]}
      </div>
      <div className="hw-sticky hw-sticky--2">
        {sticky2[0]}<br />{sticky2[1]}
      </div>

      {/* ── Тыковки у подножия ── */}
      <span className="hw-pumpkin hw-pumpkin--1"><Pumpkin /></span>
      <span className="hw-pumpkin hw-pumpkin--2"><Pumpkin /></span>

      {/* ── Ведьмочка справа + её реплика ── */}
      <div key={status} className="hw-bubble">{bubble}</div>
      <img
        src="/witch-right.png"
        className={`hw-witch hw-witch--right${reaction ? ` hw-witch--${reaction}` : ''}`}
        alt=""
        draggable="false"
      />
      <img
        src="/witch-left.png"
        className="hw-witch hw-witch--left"
        alt=""
        draggable="false"
      />

    </div>,
    document.body
  );
}
